import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

export interface DailyReport {
  _id: string;
  bd: { _id: string; fullName: string; email: string } | string;
  workDate: string;
  shiftStart: string | null;
  shiftEnd: string | null;
  totalWorkingHours: number;
  activeTrackingMinutes: number;
  idleMinutes: number;
  bidding: {
    totalBids: number;
    openBids: number;
    wonBids: number;
    lostBids: number;
    successRate: number;
    avgBidValue: number;
  };
  proposals: {
    totalGenerated: number;
    submitted: number;
    inNegotiation: number;
    winRate: number;
  };
  leads: {
    totalLeads: number;
    qualifiedLeads: number;
    convertedToOpportunities: number;
  };
  projects: {
    projectsWon: number;
    totalValueAcquired: number;
    avgProjectValue: number;
    escrowSecured: number;
  };
  connects: {
    totalUsed: number;
    avgConnectsPerBid: number;
    efficiencyRatio: number;
  };
  financials: {
    potentialRevenue: number;
    confirmedRevenue: number;
    pipelineValue: number;
  };
  productivity: {
    questionnairesGenerated: number;
    tasksCompleted: number;
    activitiesCount: number;
  };
  notes: string;
  shareToken: string;
  sharedWith: { email: string; channel: string; sentAt: string }[];
  deliveries: {
    emailSent: boolean;
    emailSentAt?: string;
    slackSent: boolean;
    slackSentAt?: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface DailyReportState {
  reports: DailyReport[];
  current: DailyReport | null;
  loading: boolean;

  fetchReports: (params?: {
    from?: string;
    to?: string;
    bdId?: string;
  }) => Promise<void>;
  fetchById: (id: string) => Promise<DailyReport | null>;
  fetchByToken: (token: string) => Promise<DailyReport | null>;
  endShift: (notes?: string) => Promise<DailyReport | null>;
  shareReport: (
    id: string,
    payload: { emails?: string[]; slack?: boolean }
  ) => Promise<boolean>;
  regenerate: (id: string) => Promise<boolean>;
}

export const useDailyReportStore = create<DailyReportState>((set, get) => ({
  reports: [],
  current: null,
  loading: false,

  fetchReports: async (params) => {
    set({ loading: true });
    try {
      const { data } = await axiosInstance.get("/daily-reports", { params });
      set({ reports: data.reports });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load reports");
    } finally {
      set({ loading: false });
    }
  },

  fetchById: async (id) => {
    set({ loading: true });
    try {
      const { data } = await axiosInstance.get(`/daily-reports/${id}`);
      set({ current: data.report });
      return data.report;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load report");
      return null;
    } finally {
      set({ loading: false });
    }
  },

  fetchByToken: async (token) => {
    set({ loading: true });
    try {
      const { data } = await axiosInstance.get(`/daily-reports/public/${token}`);
      set({ current: data.report });
      return data.report;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Report not found");
      return null;
    } finally {
      set({ loading: false });
    }
  },

  endShift: async (notes) => {
    try {
      const { data } = await axiosInstance.post("/daily-reports/end-shift", {
        notes,
      });
      toast.success("Shift ended — daily report generated");
      await get().fetchReports();
      return data.report;
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to end shift"
      );
      return null;
    }
  },

  shareReport: async (id, payload) => {
    try {
      await axiosInstance.post(`/daily-reports/${id}/share`, payload);
      toast.success("Report shared");
      return true;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to share report");
      return false;
    }
  },

  regenerate: async (id) => {
    try {
      await axiosInstance.post(`/daily-reports/${id}/regenerate`);
      toast.success("Report recomputed");
      return true;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to recompute");
      return false;
    }
  },
}));

